// Lavoriamo insieme — pagina Partner. Porting da partner.jsx (design handoff
// "Partner.html"). Due profili (reseller iscritto al ROC / partner complementare),
// catalogo servizi, proof point, 4 step, form candidatura, banda contatti.
//
// Il form di candidatura riusa il pipeline lead esistente: POST /api/v1/leads via
// api.submitLead (tipo "Contatto", fonte "partner-<profilo>"), con azienda, profilo
// e descrizione impacchettati nel messaggio. Telefono è obbligatorio perché il
// backend lo richiede (LeadService). reCAPTCHA v3 action "lead".

import { useState, type ChangeEvent, type FormEvent } from 'react'
import { Header } from '@/components/Header'
import { Footer } from '@/components/Footer'
import { Breadcrumbs } from '@/components/Breadcrumbs'
import { Icons } from '@/components/Icons'
import { openChat, CHAT_LABEL, CHAT_TAGLINE, CHAT_WA_FALLBACK } from '@/lib/chat'
import { COMPANY } from '@/data/company'
import { api } from '@/lib/api'
import { getRecaptchaToken } from '@/lib/recaptcha'
import { trackEvent } from '@/lib/analytics'
import { segnaInvioForm } from '@/lib/pageTracking'

type IconKey = keyof typeof Icons
type Feature = { icon: IconKey; t: string; d: string }

// ============================================================
// DATA
// ============================================================
const SERVIZI: Feature[] = [
  { icon: 'wifi', t: 'Connettività FTTH / FTTC / FWA', d: 'Su rete in fibra e radio di nostra proprietà. Profili residenziali e business, dove arriviamo davvero.' },
  { icon: 'phone', t: 'Telefonia VoIP e numerazioni', d: 'Linee VoIP in qualità HD e numerazioni geografiche, da attivare e portare sui clienti dei partner.' },
  { icon: 'signal', t: 'Ponti radio e collegamenti dedicati', d: 'Link punto-punto e collegamenti dedicati a banda garantita, per chi ha bisogno di una linea sua.' },
  { icon: 'headset', t: 'Centralino virtuale cloud', d: 'Centralino in cloud con IVR, code di attesa e interni ovunque: nessun hardware in sede.' },
  { icon: 'globe', t: 'IP pubblico e subnet', d: 'IP statico singolo o intere subnet dedicate, per i clienti che devono essere raggiungibili da remoto.' },
  { icon: 'sim', t: 'Mobile 5G', d: 'SIM voce e dati su rete 5G, con minuti e Giga, da affiancare alla connettività fissa.' },
]

const RESELLER_LIST: Feature[] = [
  { icon: 'tag', t: 'Margini chiari', d: 'definiti in partenza, senza sorprese.' },
  { icon: 'briefcase', t: 'Listino wholesale', d: 'dedicato, per rivendere a marchio proprio o congiunto.' },
  { icon: 'headset', t: 'Supporto pre-vendita e tecnico', d: 'al tuo fianco prima e dopo la firma.' },
  { icon: 'bolt', t: 'Attivazioni rapide', d: 'su rete nostra: i tempi li gestiamo noi.' },
]

const COMPLEMENT_LIST: Feature[] = [
  { icon: 'shield', t: 'Nessuna sovrapposizione', d: 'la connettività la mettiamo noi, i tuoi servizi restano tuoi.' },
  { icon: 'person', t: 'Referente tecnico diretto', d: 'una persona con cui parli, non un ticket.' },
  { icon: 'pin', t: 'Intervento sul territorio', d: 'tecnici che vengono di persona dai tuoi clienti.' },
  { icon: 'bolt', t: 'Reattività sui guasti', d: 'la rete è nostra, il problema lo risolviamo noi.' },
]

const WHY: Feature[] = [
  { icon: 'router', t: 'Rete di proprietà', d: 'Fibra e radio nostre a Ravenna. Quando qualcosa si rompe, <strong>lo risolviamo noi</strong> — non dipendiamo da terzi e non rimbalziamo la palla.' },
  { icon: 'headset', t: 'Assistenza umana', d: 'Niente call center. Risponde <strong>una persona</strong>, spesso la stessa, che conosce te e i tuoi clienti.' },
  { icon: 'pin', t: 'Presenza fisica', d: 'Sede a Ravenna e <strong>tecnici che intervengono di persona</strong> sul territorio. Non siamo un numero verde.' },
  { icon: 'person', t: 'Un referente diretto', d: 'Come partner hai una persona di riferimento. <strong>Niente coda</strong>, niente script: ci parli direttamente.' },
  { icon: 'calendar', t: `Indipendenti dal ${COMPANY.foundedYear}`, d: `Operatore di telecomunicazioni indipendente da Ravenna: <strong>${COMPANY.clients} clienti</strong> e rete su <strong>${COMPANY.comuniCount} comuni</strong> del territorio.` },
]

const STEPS = [
  { n: 1, t: 'Contatto', d: 'Ci scrivi o ci chiami. Ci raccontiamo a vicenda chi siamo e cosa cerchiamo.' },
  { n: 2, t: 'Valutazione', d: 'Guardiamo insieme il tuo profilo e i volumi, per capire la forma giusta di collaborazione.' },
  { n: 3, t: 'Accordo', d: 'Reseller o referral: scegliamo il modello che funziona per entrambi e lo mettiamo nero su bianco.' },
  { n: 4, t: 'Onboarding', d: 'Parti con un referente tecnico dedicato che ti segue dalle prime attivazioni in poi.' },
]

const TIPO_LABELS: Record<string, string> = {
  reseller: 'Reseller iscritto al ROC',
  complementare: 'Partner complementare',
  altro: 'Altro',
}

const MAPS_URL = `https://maps.google.com/?q=${encodeURIComponent(`${COMPANY.address.street} ${COMPANY.address.postalCode} ${COMPANY.address.locality}`)}`

function FeatureIcon({ name }: { name: IconKey }) {
  const C = Icons[name]
  return <C />
}

// ============================================================
// FORM CANDIDATURA
// ============================================================
type Status = 'idle' | 'sending' | 'ok' | 'error'

function PartnerForm() {
  const [form, setForm] = useState({ azienda: '', referente: '', email: '', telefono: '', tipo: '', descrizione: '' })
  const [consent, setConsent] = useState(false)
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<string | null>(null)

  const set = (k: keyof typeof form) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  const valid = !!(form.azienda.trim() && form.referente.trim() && form.email.trim() && form.telefono.trim() && form.tipo && consent)

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    if (!valid) {
      setError('Compila i campi obbligatori e accetta l’informativa privacy.')
      return
    }
    setStatus('sending')
    try {
      const recaptchaToken = await getRecaptchaToken('lead')
      const tipoLabel = TIPO_LABELS[form.tipo] ?? form.tipo
      const messaggio = [
        `Candidatura partner — ${tipoLabel}`,
        `Azienda: ${form.azienda.trim()}`,
        form.descrizione.trim() ? `Attività: ${form.descrizione.trim()}` : null,
      ].filter(Boolean).join('\n')
      await api.submitLead({
        tipo: 'Contatto',
        nome: form.referente.trim(),
        telefono: form.telefono.trim(),
        email: form.email.trim(),
        messaggio,
        consensoPrivacy: consent,
        fonte: `partner-${form.tipo}`,
        recaptchaToken,
      })
      // Conversione: prima mancava, il lead arrivava nel CRM ma GA4 non lo vedeva.
      trackEvent('lead_inviato', { fonte: `partner-${form.tipo}` })
      segnaInvioForm()
      setStatus('ok')
    } catch {
      setStatus('error')
      setError(`Non è stato possibile inviare la candidatura. Riprova o scrivici a ${COMPANY.email}.`)
    }
  }

  if (status === 'ok') {
    const nome = form.referente.trim().split(' ')[0]
    return (
      <div className="pt-form-r">
        <div className="pt-form">
          <div className="pt-form-sent">
            <span className="ic"><Icons.check /></span>
            <h3>Candidatura inviata.</h3>
            <p>Grazie, {nome || 'a presto'}. Ti ricontattiamo a breve da Ravenna — una persona vera, non un bot.</p>
          </div>
        </div>
      </div>
    )
  }

  const sending = status === 'sending'

  return (
    <div className="pt-form-r">
      <h3>Candidati come partner</h3>
      <p className="tag">Compili, ci pensiamo noi. Nessun impegno: prima ci conosciamo.</p>
      <form className="pt-form" data-form="partner" onSubmit={submit} noValidate>
        <div className="pt-field full">
          <label>Ragione sociale <span className="req">*</span></label>
          <input type="text" value={form.azienda} onChange={set('azienda')} placeholder="Es. Rossi Informatica S.r.l." required />
        </div>
        <div className="pt-field">
          <label>Referente <span className="req">*</span></label>
          <input type="text" value={form.referente} onChange={set('referente')} placeholder="Nome e cognome" autoComplete="name" required />
        </div>
        <div className="pt-field">
          <label>Telefono <span className="req">*</span></label>
          <input type="tel" value={form.telefono} onChange={set('telefono')} placeholder="Es. 0544 000000" autoComplete="tel" required />
        </div>
        <div className="pt-field full">
          <label>Email <span className="req">*</span></label>
          <input type="email" value={form.email} onChange={set('email')} placeholder="nome@azienda.it" autoComplete="email" required />
        </div>
        <div className="pt-field full">
          <label>Tipo di partner <span className="req">*</span></label>
          <select value={form.tipo} onChange={set('tipo')} required>
            <option value="" disabled>Seleziona il profilo…</option>
            <option value="reseller">Reseller iscritto al ROC</option>
            <option value="complementare">Partner complementare</option>
            <option value="altro">Altro</option>
          </select>
        </div>
        <div className="pt-field full">
          <label>Breve descrizione dell&apos;attività</label>
          <textarea value={form.descrizione} onChange={set('descrizione')} placeholder="Di cosa ti occupi, dove lavori, che clienti segui…" />
        </div>
        <label className="pt-consent">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} required />
          <span>Ho letto e accetto l&apos;<a href="/privacy" target="_blank" rel="noopener noreferrer">informativa sulla privacy</a> e acconsento al trattamento dei dati per essere ricontattato. <span className="req">*</span></span>
        </label>
        {error && <p className="pt-form-error" role="alert">{error}</p>}
        <div className="pt-recaptcha">
          <Icons.shield />
          <span>Protetto da reCAPTCHA. Non condividiamo i tuoi dati con nessuno.</span>
        </div>
        <div className="pt-form-submit">
          <button type="submit" className="cta-grad" disabled={!valid || sending}>
            {sending ? 'Invio…' : <>Invia candidatura <Icons.arrow /></>}
          </button>
        </div>
      </form>
    </div>
  )
}

// ============================================================
// PAGINA
// ============================================================
export function Partner() {
  return (
    <>
      <Header />
      <Breadcrumbs items={[
        { label: 'Home', href: '/' },
        { label: 'Lavoriamo insieme' },
      ]} />

      {/* HERO */}
      <section className="page-hero">
        <div className="page-hero-inner">
          <div>
            <div className="eyebrow">
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#11B1E9', boxShadow: '0 0 10px #11B1E9' }} />
              Lavoriamo insieme
            </div>
            <h1>Un interlocutore unico per le telecomunicazioni dei tuoi clienti, <span className="g">sul territorio.</span></h1>
            <p className="lead">Rete di proprietà a Ravenna, assistenza umana e reattiva, presenza fisica reale. Un solo referente per connettività, telefonia, ponti radio e centralini.</p>
            <div className="pt-hero-tags">
              <span><Icons.handshake /> Reseller e partner complementari</span>
              <span><Icons.router /> Rete in fibra e radio nostra</span>
            </div>
          </div>
          <aside className="page-hero-side pt-side">
            <div className="pt-side-row"><span className="icn"><Icons.shield /></span><span><strong>Operatore indipendente dal {COMPANY.foundedYear}</strong><span className="s">Da Ravenna, {COMPANY.clients} clienti su {COMPANY.comuniCount} comuni</span></span></div>
            <div className="pt-side-row"><span className="icn"><Icons.router /></span><span><strong>Il guasto lo risolviamo noi</strong><span className="s">Rete in fibra e radio di proprietà</span></span></div>
            <div className="pt-side-row"><span className="icn"><Icons.headset /></span><span><strong>Risponde una persona</strong><span className="s">Nessun call center, un referente diretto</span></span></div>
            <div className="pt-side-row"><span className="icn"><Icons.pin /></span><span><strong>Presenza fisica sul territorio</strong><span className="s">Sede a Ravenna, tecnici che intervengono di persona</span></span></div>
          </aside>
        </div>
      </section>

      {/* DUE PROFILI PARTNER */}
      <section className="pt-profiles-section">
        <div className="section-inner">
          <div className="section-head center">
            <span className="kicker">Due modi di lavorare insieme</span>
            <h2>Scegli il profilo <span className="g">che è il tuo.</span></h2>
            <p>Che tu rivenda la nostra linea o cerchi un fornitore affidabile per i tuoi clienti, c&apos;è una strada fatta su misura.</p>
          </div>
          <div className="pt-profiles">

            <article className="pt-profile is-reseller">
              <div className="pt-profile-top">
                <span className="pt-profile-ic"><Icons.tag /></span>
                <div>
                  <div className="pt-profile-kind">Reseller · iscritti al ROC</div>
                  <h3>Rivendi la nostra linea</h3>
                </div>
              </div>
              <p className="pt-profile-lead">Rivendi la connettività Spadhausen a marchio proprio o congiunto. Noi mettiamo la rete e il supporto, tu il rapporto con il cliente.</p>
              <div className="pt-profile-divider">Cosa trovi</div>
              <ul className="pt-profile-list">
                {RESELLER_LIST.map((it) => (
                  <li key={it.t}><FeatureIcon name={it.icon} /><span><strong>{it.t}</strong> — {it.d}</span></li>
                ))}
              </ul>
              <div className="pt-profile-cta">
                <a href="#candidatura" className="cta-ghost">Diventa reseller <Icons.arrow /></a>
              </div>
            </article>

            <article className="pt-profile is-complement">
              <div className="pt-profile-top">
                <span className="pt-profile-ic"><Icons.layers /></span>
                <div>
                  <div className="pt-profile-kind">Partner complementari</div>
                  <h3>Porti la nostra rete ai tuoi clienti</h3>
                </div>
              </div>
              <p className="pt-profile-lead">Hai già il tuo business — assistenza informatica, system integration, installazioni — e ti serve un fornitore di connettività affidabile per i tuoi clienti.</p>
              <div className="pt-profile-divider">Cosa trovi</div>
              <ul className="pt-profile-list">
                {COMPLEMENT_LIST.map((it) => (
                  <li key={it.t}><FeatureIcon name={it.icon} /><span><strong>{it.t}</strong> — {it.d}</span></li>
                ))}
              </ul>
              <div className="pt-profile-cta">
                <a href="#candidatura" className="cta-grad">Diventa partner <Icons.arrow /></a>
              </div>
            </article>

          </div>
        </div>
      </section>

      {/* COSA OFFRIAMO */}
      <section className="pt-serv-section">
        <div className="section-inner">
          <div className="section-head">
            <span className="kicker">Cosa offriamo ai partner</span>
            <h2>Tutta la telecomunicazione, <span className="g">da un solo fornitore.</span></h2>
            <p>Un catalogo completo su rete nostra: connettività, voce e servizi cloud per i clienti dei nostri partner.</p>
          </div>
          <div className="pt-serv-grid">
            {SERVIZI.map((s) => (
              <div className="pt-serv" key={s.t}>
                <span className="pt-serv-ic"><FeatureIcon name={s.icon} /></span>
                <h4>{s.t}</h4>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PERCHÉ SPADHAUSEN */}
      <section className="pt-why-section">
        <div className="section-inner">
          <div className="section-head">
            <span className="kicker">Perché Spadhausen come partner</span>
            <h2>Fatti concreti, <span className="g">non slogan.</span></h2>
            <p>Quello che cambia davvero quando dietro c&apos;è una rete di proprietà e una persona che risponde.</p>
          </div>
          <div className="pt-why-grid">
            {WHY.map((w, i) => (
              <div className={`pt-why ${i === WHY.length - 1 ? 'is-wide' : ''}`} key={w.t}>
                <span className="pt-why-ic"><FeatureIcon name={w.icon} /></span>
                <div className="pt-why-tx">
                  <h4>{w.t}</h4>
                  <p dangerouslySetInnerHTML={{ __html: w.d }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COME FUNZIONA */}
      <section className="pt-steps-section">
        <div className="pt-steps-inner">
          <div className="pt-steps-head">
            <span className="kicker">Come funziona</span>
            <h2>Dalla prima telefonata <span className="g">alla prima attivazione.</span></h2>
            <p>Niente percorsi a ostacoli. Quattro passi semplici, con una persona che ti segue.</p>
          </div>
          <div className="pt-steps">
            {STEPS.map((s) => (
              <div className="pt-step" key={s.n}>
                <span className="n">{s.n}</span>
                <h4>{s.t}</h4>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM CANDIDATURA */}
      <section className="pt-form-section" id="candidatura">
        <div className="section-inner">
          <div className="pt-form-grid">
            <div className="pt-form-l">
              <span className="kicker">Diventa partner</span>
              <h2>Raccontaci chi sei. <span className="g">Ci sentiamo presto.</span></h2>
              <p>Lasciaci due righe sulla tua attività. Valutiamo insieme il profilo giusto e ti ricontattiamo da Ravenna.</p>
              <ul className="pt-form-points">
                <li><span className="ic"><Icons.headset /></span><span><strong>Ti risponde una persona</strong>spesso la stessa, da Ravenna.</span></li>
                <li><span className="ic"><Icons.check /></span><span><strong>Nessun impegno</strong>prima ci conosciamo, poi decidiamo insieme.</span></li>
                <li><span className="ic"><Icons.person /></span><span><strong>Referente dedicato</strong>che ti segue dall&apos;onboarding in poi.</span></li>
              </ul>
            </div>
            <PartnerForm />
          </div>
        </div>
      </section>

      {/* CHIUSURA + CONTATTI */}
      <section className="pt-close-section">
        <div className="pt-close-inner">
          <div className="pt-close-l">
            <span className="kicker">Parliamone</span>
            <h2>Un nodo del territorio, <span className="g">aperto a chi lo vive.</span></h2>
            <p>Spadhausen è un pezzo della rete di Ravenna e Romagna. Se lavori qui e cerchi un interlocutore unico per le telecomunicazioni, c&apos;è posto per collaborare.</p>
          </div>
          <div className="pt-close-channels">
            <a className="pt-close-ch pt-close-ch--chat" href={CHAT_WA_FALLBACK} target="_blank" rel="noopener noreferrer" onClick={openChat}>
              <span className="ic"><Icons.chat /></span>
              <span><strong>{CHAT_LABEL}</strong><span>{CHAT_TAGLINE}</span></span>
            </a>
            <a className="pt-close-ch" href={COMPANY.phoneHref}>
              <span className="ic"><Icons.phone /></span>
              <span><strong>{COMPANY.phone}</strong><span>Lun–Ven 9:00–18:00</span></span>
            </a>
            <a className="pt-close-ch" href={`mailto:${COMPANY.email}`}>
              <span className="ic"><Icons.mail /></span>
              <span><strong>{COMPANY.email}</strong><span>Risposta entro 4 ore</span></span>
            </a>
            <a className="pt-close-ch" href={MAPS_URL} target="_blank" rel="noopener noreferrer">
              <span className="ic"><Icons.pin /></span>
              <span><strong>{COMPANY.address.street}, {COMPANY.address.locality}</strong><span>Sede e tecnici sul territorio</span></span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  )
}
